import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { AutoComplete } from 'primereact/autocomplete';
import '../../css/Pages.css';
import BookPublicationListing from '../../components/ScientificManagement/BookPublication/BookPublicationListing';
import BookPublicationForm from '../../components/ScientificManagement/BookPublication/BookPublicationForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';
import { ALL_ISO_CODES } from '../../data/isoCodes';
import { COUNTRIES_DATA } from '../../data/countriesData';
import { downloadExcel } from '../../utils/excelUtils';
import DuplicateWarningModal from '../../components/DuplicateWarningModal';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, ...rest }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            {...rest}
        />
    );
};

const BookPublication = () => {
    const today = useMemo(() => new Date().toISOString().split('T')[0], []);
    const toast = useRef(null);

    const initialForm = useMemo(() => ({
        UserId: '', Title: '', Publisher: '', PublicationDate: today, PublicationPlace: '',
        Isbn: '', Language: 'Tiếng Việt', ProjectType: '', Country: 'Việt Nam', EducationLevel: '',
        PublicationNumber: '', TotalAuthors: 1, MainAuthors: 1, OriginalAuthorsList: '',
        MembersJSON: '[]', AttachmentUrl: '', Note: ''
    }), [today]);

    const [data, setData] = useState([]);
    const [users, setUsers] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [countries, setCountries] = useState([]);
    const [projectTypes, setProjectTypes] = useState([]);
    const [educationLevels, setEducationLevels] = useState([]);
    const [departments, setDepartments] = useState([]);

    const [duplicateGroups, setDuplicateGroups] = useState([]);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

    const [isLoading, setIsLoading] = useState(true);
    const [modalMode, setModalMode] = useState(null);
    const isModalOpen = modalMode !== null;
    const [formData, setFormData] = useState(initialForm);
    const [editId, setEditId] = useState(null);
    const [members, setMembers] = useState([]);

    const [isEditRequestModalOpen, setIsEditRequestModalOpen] = useState(false);
    const [editRequestContent, setEditRequestContent] = useState('');
    const [selectedBookForEdit, setSelectedBookForEdit] = useState(null);

    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [rejectContent, setRejectContent] = useState('');
    const [selectedRejectId, setSelectedRejectId] = useState(null);

    const [filters, setFilters] = useState({
        department: '',
        authorId: null,
        keyword: '',
        status: '',
        fromDate: null,
        toDate: null
    });

    const [sortConfig, setSortConfig] = useState({ key: 'Title', direction: 'asc' });

    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [filteredAuthors, setFilteredAuthors] = useState([]);

    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [previewExportData, setPreviewExportData] = useState(null);
    const [exportFilters, setExportFilters] = useState({
        fromDate: null,
        toDate: null,
        projectType: '',
        educationLevel: '',
        userId: ''
    });

    const [isDesktop, setIsDesktop] = useState(true);
    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
    const userRoleName = useMemo(() => (currentUser.RoleName || '').toLowerCase(), [currentUser.RoleName]);

    const isManager = useMemo(() =>
        currentUser.RoleId === 1 || userRoleName === 'quản trị viên' ||
        currentUser.RoleId === 11 || userRoleName.includes('quản lý khoa'),
        [userRoleName, currentUser.RoleId]
    );

    const canModify = useMemo(() =>
        currentUser.RoleId === 1 || userRoleName === 'quản trị viên' ||
        ((currentUser.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser.IsDelegatedAdmin === true),
        [userRoleName, currentUser.RoleId, currentUser.IsDelegatedAdmin]
    );

    const authHeaders = useMemo(() => ({
        'X-User-Role-Id': currentUser.RoleId ? currentUser.RoleId.toString() : '',
        'X-User-Dept-Id': currentUser.DepartmentId ? currentUser.DepartmentId.toString() : '',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
    }), [currentUser]);

    const userOptions = useMemo(() => {
        return users.map(u => {
            const labelStr = `${u.LastName || ''} ${u.FirstName || ''} - ${u.Email || ''}`.trim();
            return {
                label: labelStr,
                searchKey: superCleanString(labelStr),
                value: u.Id
            };
        });
    }, [users]);

    const userFilterTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    const filterUserOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || '';
            const dept = u.DepartmentName || '';
            const rawSearchStr = `${fullName} ${email} ${dept}`;

            return {
                label: fullName,
                searchKey: superCleanString(rawSearchStr),
                value: u.Id,
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const searchAuthor = (event) => {
        const query = superCleanString(event.query);
        let _filteredAuthors;

        if (!query.trim().length) {
            _filteredAuthors = [...filterUserOptions];
        } else {
            _filteredAuthors = filterUserOptions.filter((user) => {
                return user.searchKey.includes(query);
            });
        }
        setFilteredAuthors(_filteredAuthors);
    };

    const filteredData = useMemo(() => {
        let allowedDeptNames = [];
        if (filters.department) {
            const selectedDept = departments.find(d => d.Name.trim() === filters.department.trim());

            if (selectedDept) {
                allowedDeptNames.push(selectedDept.Name.trim());

                const findChildren = (parentId) => {
                    const children = departments.filter(d => d.ParentId === parentId);
                    children.forEach(child => {
                        allowedDeptNames.push(child.Name.trim());
                        findChildren(child.Id);
                    });
                };

                findChildren(selectedDept.Id);
            } else {
                allowedDeptNames.push(filters.department.trim());
            }
        }

        let result = data.filter(item => {
            if (filters.department) {
                const itemDeptName = item.DepartmentName ? item.DepartmentName.trim() : "";
                if (!allowedDeptNames.includes(itemDeptName)) {
                    return false;
                }
            }

            if (filters.status && item.Status !== filters.status) return false;

            if (item.PublicationDate) {
                const itemPubDate = new Date(item.PublicationDate);
                itemPubDate.setHours(0, 0, 0, 0);

                if (filters.fromDate) {
                    const filterFrom = new Date(filters.fromDate);
                    filterFrom.setHours(0, 0, 0, 0);
                    if (itemPubDate < filterFrom) return false;
                }
                if (filters.toDate) {
                    const filterTo = new Date(filters.toDate);
                    filterTo.setHours(0, 0, 0, 0);
                    if (itemPubDate > filterTo) return false;
                }
            } else if (filters.fromDate || filters.toDate) {
                return false;
            }

            if (filters.authorId) {
                const targetId = filters.authorId.toString();
                let isMatch = item.UserId?.toString() === targetId;

                if (!isMatch) {
                    try {
                        const mList = JSON.parse(item.MembersJSON || '[]');
                        isMatch = mList.some(m => (m.UserId || m.userId || m.Id)?.toString() === targetId);
                    } catch (e) {
                        isMatch = false;
                    }
                }

                if (!isMatch) return false;
            }

            if (filters.keyword) {
                const kw = superCleanString(filters.keyword);
                const bookTitle = superCleanString(item.Title);
                const bookAuthors = superCleanString(item.OriginalAuthorsList);
                const bookPublisher = superCleanString(item.Publisher);

                if (!bookTitle.includes(kw) && !bookAuthors.includes(kw) && !bookPublisher.includes(kw)) {
                    return false;
                }
            }
            return true;
        });

        if (sortConfig !== null) {
            result.sort((a, b) => {
                if (sortConfig.key === 'PublicationDate') {
                    const dateA = a.PublicationDate ? new Date(a.PublicationDate).getTime() : 0;
                    const dateB = b.PublicationDate ? new Date(b.PublicationDate).getTime() : 0;
                    if (dateA < dateB) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (dateA > dateB) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                } else {
                    const valA = (a[sortConfig.key] || '').toString().trim();
                    const valB = (b[sortConfig.key] || '').toString().trim();
                    const comparison = valA.localeCompare(valB, 'vi', { sensitivity: 'base' });
                    return sortConfig.direction === 'asc' ? comparison : -comparison;
                }
            });
        }

        return result;

    }, [data, filters, departments, sortConfig]);

    const handleFetchDuplicates = async () => {
        setIsCheckingDuplicates(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/detect-duplicates?type=book`, {
                method: 'GET',
                headers: authHeaders
            });

            if (res.ok) {
                const groups = await res.json();
                setDuplicateGroups(groups);
                setIsDuplicateModalOpen(true);

                if (groups.length === 0) {
                    toast.current?.show({ severity: 'info', summary: 'Thông báo', detail: 'Không phát hiện Sách/Giáo trình nào trùng lặp!' });
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể lấy dữ liệu trùng lặp từ hệ thống!' });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Mất kết nối đến máy chủ!' });
        } finally {
            setIsCheckingDuplicates(false);
        }
    };

    const fetchDropdowns = useCallback(async () => {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;

        try {
            const [uRes, ptRes, elRes, deptRes] = await Promise.all([
                fetch(`${baseUrl}/user?mode=selection`, { headers: authHeaders }),
                fetch(`${baseUrl}/projecttype`, { headers: authHeaders }),
                fetch(`${baseUrl}/educationlevel`, { headers: authHeaders }),
                fetch(`${baseUrl}/department`, { headers: authHeaders })
            ]);

            if (uRes.ok) setUsers(await uRes.json());
            if (ptRes.ok) setProjectTypes(await ptRes.json());
            if (elRes.ok) setEducationLevels(await elRes.json());
            if (deptRes.ok) setDepartments(await deptRes.json());
        } catch (error) {
            console.error("Lỗi tải danh mục hệ thống:", error);
        }

        setCountries(COUNTRIES_DATA);

        try {
            const displayNames = new Intl.DisplayNames(['vi'], { type: 'language' });
            const formattedLanguages = ALL_ISO_CODES.map(code => {
                let nameVi = displayNames.of(code) || code;
                return { Id: code.toUpperCase(), Name: nameVi.charAt(0).toUpperCase() + nameVi.slice(1) };
            }).filter(lang => lang.Name.toLowerCase() !== lang.Id.toLowerCase()).sort((a, b) => a.Name.localeCompare(b.Name));

            setLanguages(formattedLanguages);
        } catch (error) {
            console.error("Lỗi khởi tạo ngôn ngữ:", error);
        }

    }, [authHeaders]);

    const fetchData = useCallback(async (isBackground = false) => {
        if (!currentUser?.Id) return;
        if (!isBackground) setIsLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/bookpublication?userId=${currentUser.Id}&isAdmin=${isManager}`,
                {
                    method: 'GET',
                    headers: authHeaders
                }
            );
            if (response.ok) {
                const result = await response.json();
                setData(result);
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi tải dữ liệu!' });
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, [currentUser?.Id, isManager, authHeaders]);

    useEffect(() => {
        fetchData();
        fetchDropdowns();
    }, [fetchData, fetchDropdowns]);

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);

    const updateStatusWithNote = useCallback(async (id, newStatus, note) => {
        if (!canModify) {
            toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền thực hiện thao tác này!', life: 3000 });
            return false;
        }

        try {
            const userId = currentUser.Id || '';
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bookpublication?userId=${userId}&isAdmin=${isManager}`, {
                method: 'PATCH',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify({ id: id, status: newStatus, note: note })
            });

            if (response.ok) {
                const messages = {
                    'Đã duyệt': "Phê duyệt thành công!",
                    'Từ chối': "Đã từ chối bản kê khai này!",
                    'Đã gửi duyệt': "Đã hủy trạng thái duyệt!"
                };
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: messages[newStatus] || 'Cập nhật trạng thái thành công!', life: 3000 });
                fetchData(true);
                return true;
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Thao tác thất bại!', life: 3000 });
                return false;
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
            return false;
        }
    }, [canModify, currentUser.Id, isManager, authHeaders, fetchData]);

    const handleStatusChange = useCallback(async (id, newStatus) => {
        await updateStatusWithNote(id, newStatus, '');
    }, [updateStatusWithNote]);

    const handleBulkStatusChange = async (ids, newStatus) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền phê duyệt!', life: 3000 });
        }

        if (newStatus === 'Đã duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status === 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã duyệt để thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const userId = currentUser.Id || '';
            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/bookpublication?userId=${userId}&isAdmin=${isManager}`, {
                    method: 'PATCH',
                    headers: { ...authHeaders, 'Content-Type': 'application/json; charset=UTF-8' },
                    body: JSON.stringify({ id, status: newStatus, note: '' })
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current?.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã duyệt' ? `Đã phê duyệt ${ids.length} mục!` : `Đã hủy duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleRejectWithModal = useCallback((id) => {
        if (!canModify) {
            toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền từ chối!', life: 3000 });
            return;
        }
        setSelectedRejectId(id);
        setRejectContent('');
        setIsRejectModalOpen(true);
    }, [canModify]);

    const handleConfirmReject = async (e) => {
        e.preventDefault();
        if (!rejectContent.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập lý do từ chối!', life: 3000 });
            return;
        }
        const success = await updateStatusWithNote(selectedRejectId, 'Từ chối', rejectContent);
        if (success) {
            setIsRejectModalOpen(false);
            setRejectContent('');
            setSelectedRejectId(null);
        }
    };

    const handleOpenRequestEdit = useCallback((item) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền yêu cầu chỉnh sửa!', life: 3000 });
        }
        setSelectedBookForEdit(item);
        setEditRequestContent('');
        setIsEditRequestModalOpen(true);
    }, [canModify]);

    const handleSendEditRequest = async (e) => {
        e.preventDefault();
        if (!editRequestContent.trim()) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập nội dung yêu cầu!', life: 3000 });
        }

        const success = await updateStatusWithNote(selectedBookForEdit.Id, 'Yêu cầu chỉnh sửa', editRequestContent);
        if (success) {
            setIsEditRequestModalOpen(false);
            setEditRequestContent('');
            setSelectedBookForEdit(null);
        }
    };

    const checkAndFetchMissingUsers = useCallback(async (parsedMembers) => {
        const missingIds = parsedMembers
            .map(m => m.userId || m.UserId || m.Id || m.id)
            .filter(id => id && !users.some(u => u.Id.toString() === id.toString()));

        if (missingIds.length > 0) {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?mode=selection&includeIds=${missingIds.join(',')}`, {
                    headers: authHeaders
                });

                if (res.ok) {
                    const extraUsers = await res.json();
                    setUsers(prevUsers => {
                        const merged = [...prevUsers];
                        extraUsers.forEach(eu => {
                            if (!merged.some(u => u.Id === eu.Id)) merged.push(eu);
                        });
                        return merged;
                    });
                }
            } catch (error) {
                console.error("Lỗi khi tải thông tin người dùng bổ sung:", error);
            }
        }
    }, [users, authHeaders]);

    const handleEdit = useCallback(async (item) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép sửa dữ liệu!', life: 3000 });
        }
        if (item.Status === 'Đã duyệt') {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Bài báo đã duyệt, không thể sửa. Hãy hủy duyệt trước!', life: 4000 });
        }
        if (item.IsPendingDeletion) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không thể sửa sách đang trong trạng thái chờ duyệt xóa!', life: 4000 });
        }

        let parsedMembers = [];
        try { parsedMembers = JSON.parse(item.MembersJSON || '[]'); } catch (e) { parsedMembers = []; }

        await checkAndFetchMissingUsers(parsedMembers);

        setEditId(item.Id);
        setFormData({
            ...initialForm,
            ...item,
            UserId: item.UserId || '',
            ProjectType: item.ProjectType || '',
            EducationLevel: item.EducationLevel || '',
            AttachmentUrl: item.AttachmentUrl || '',
            Note: item.Note || ''
        });
        setMembers(parsedMembers);
        setModalMode('edit');
    }, [canModify, initialForm, checkAndFetchMissingUsers]);

    const handleViewDetail = useCallback(async (item) => {
        let parsedMembers = [];
        try { parsedMembers = JSON.parse(item.MembersJSON || '[]'); } catch (e) { parsedMembers = []; }

        await checkAndFetchMissingUsers(parsedMembers);

        setEditId(item.Id);
        setFormData({
            ...initialForm,
            ...item,
            UserId: item.UserId || '',
            ProjectType: item.ProjectType || '',
            EducationLevel: item.EducationLevel || '',
            AttachmentUrl: item.AttachmentUrl || '',
            Note: item.Note || ''
        });
        setMembers(parsedMembers);
        setModalMode('view');
    }, [initialForm, checkAndFetchMissingUsers]);

    const handleDelete = useCallback((id) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép xóa!', life: 3000 });
        }

        const item = data.find(d => d.Id === id);
        if (item?.Status === 'Đã duyệt') {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không thể xóa công trình đã duyệt!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa công trình này?',
            accept: async () => {
                try {
                    const userId = currentUser.Id || '';
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bookpublication?id=${id}&userId=${userId}&isAdmin=${isManager}`, {
                        method: 'DELETE',
                        headers: authHeaders
                    });

                    if (res.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Xóa thành công!' });
                        fetchData(true);
                    } else {
                        let errData;
                        try { errData = await res.json(); } catch (e) { errData = { message: "Xóa thất bại!" }; }
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: errData.message });
                    }
                } catch (e) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi máy chủ!' });
                }
            }
        });
    }, [canModify, data, currentUser.Id, isManager, authHeaders, fetchData, confirmDeleteDialog]);

    const handleRequestDelete = useCallback((id) => {
        if (!canModify) return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem!' });

        confirmDeleteDialog({
            header: 'Yêu cầu Xóa',
            message: 'Vì lý do bảo mật, dữ liệu sẽ không bị xóa ngay. Hệ thống sẽ tạo "Yêu cầu xóa" và cần 1 Admin quản lý khác vào duyệt. Bạn có tiếp tục?',
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bookpublication?id=${id}&action=request`, {
                        method: 'DELETE', headers: authHeaders
                    });
                    if (response.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Đã gửi yêu cầu', detail: 'Vui lòng chờ Admin quản lý duyệt xóa!' });
                        fetchData(true);
                    } else {
                        const result = await response.json();
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: result.message || 'Thao tác thất bại!' });
                    }
                } catch (e) { toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi máy chủ!' }); }
            }
        });
    }, [canModify, authHeaders, fetchData, confirmDeleteDialog]);

    const handleApproveDelete = useCallback((id) => {
        confirmDeleteDialog({
            header: 'CẢNH BÁO: DUYỆT XÓA VĨNH VIỄN',
            message: 'Hành động này sẽ XÓA VĨNH VIỄN sách khỏi hệ thống. Bạn có chắc chắn muốn duyệt yêu cầu này?',
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bookpublication?id=${id}&action=approve`, {
                        method: 'DELETE', headers: authHeaders
                    });
                    if (response.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Đã xóa', detail: 'Dữ liệu đã bị xóa vĩnh viễn.' });
                        fetchData(true);
                    } else {
                        const result = await response.json();
                        toast.current?.show({ severity: 'error', summary: 'Từ chối', detail: result.message || 'Thao tác thất bại!' });
                    }
                } catch (e) { toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi máy chủ!' }); }
            }
        });
    }, [authHeaders, fetchData, confirmDeleteDialog]);

    const handleCancelDeleteRequest = useCallback((id) => {
        fetch(`${process.env.REACT_APP_API_BASE_URL}/bookpublication?id=${id}&action=cancel`, {
            method: 'DELETE', headers: authHeaders
        }).then(async res => {
            if (res.ok) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã hủy yêu cầu xóa.' });
                fetchData(true);
            } else {
                const result = await res.json();
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: result.message });
            }
        }).catch(() => toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi máy chủ!' }));
    }, [authHeaders, fetchData]);

    const handleBulkDelete = (ids) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép xóa!', life: 3000 });
        }
        const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status === 'Đã duyệt');
        if (invalidItems.length > 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các công trình đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận tạo yêu cầu xóa hàng loạt',
            message: `Bạn có chắc chắn muốn tạo yêu cầu xóa cho ${ids.length} sách đã chọn?`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/bookpublication?id=${id}&action=request`, {
                            method: 'DELETE', headers: authHeaders
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: `Đã gửi yêu cầu xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi gửi yêu cầu thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const closeModal = useCallback(() => {
        setModalMode(null);
        setFormData(initialForm);
        setMembers([]);
        setEditId(null);
    }, [initialForm]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (modalMode === 'view') return;

        if (editId) {
            const currentItem = data.find(d => d.Id === editId);
            if (currentItem?.Status === 'Đã duyệt') {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Dữ liệu đã duyệt, không thể lưu!', life: 3000 });
            }
        }

        if (!currentUser.Id) {
            return toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Hết phiên đăng nhập!' });
        }
        if (members.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng chọn ít nhất một thành viên!' });
        }

        let finalOwnerId = currentUser.Id;

        if (isManager) {
            const leader = members.find(m => m.role === "Chủ biên" || m.Role === "Chủ biên");
            if (leader) {
                const leaderId = leader.userId || leader.UserId || leader.Id || leader.id;
                if (leaderId) {
                    finalOwnerId = leaderId;
                } else {
                    toast.current?.show({ severity: 'warn', summary: 'Lỗi', detail: 'Thành viên "Chủ biên" chưa được chọn hệ thống!', life: 5000 });
                    return;
                }
            } else {
                toast.current?.show({ severity: 'warn', summary: 'Lỗi', detail: 'Vui lòng chỉ định ít nhất một "Chủ biên" cho sách/giáo trình!', life: 5000 });
                return;
            }
        }

        const payload = {
            ...formData,
            UserId: parseInt(finalOwnerId),
            MembersJSON: JSON.stringify(members),
            Id: editId ? editId : 0
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bookpublication`, {
                method: editId ? 'PUT' : 'POST',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            let result;
            try {
                result = await response.json();
            } catch (err) {
                result = { status: 'error', message: 'Lỗi phản hồi từ máy chủ!' };
            }

            if (response.ok && result.status !== 'error') {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: isManager && finalOwnerId !== currentUser.Id ? 'Đã kê khai hộ thành công!' : 'Lưu dữ liệu thành công!'
                });
                fetchData(true);
                closeModal();
            } else {
                const errorMsg = result.message || 'Lưu dữ liệu thất bại!';
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: errorMsg, life: 6000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
        }
    };

    const executeExport = useCallback((dataToExport, fileSuffix) => {
        if (dataToExport.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        const formattedData = dataToExport.map((item, index) => {
            let leaderName = '';
            try {
                const parsedMembers = JSON.parse(item.MembersJSON || '[]');
                const leader = parsedMembers.find(m => m.role === 'Chủ biên' || m.Role === 'Chủ biên');
                if (leader) {
                    leaderName = leader.fullName || leader.FullName || '';
                }
            } catch (e) { console.error(e); }

            return {
                'STT': index + 1,
                'Tên Sách/Giáo trình': item.Title || '',
                'Chủ biên': leaderName,
                'Tác giả nguyên bản': item.OriginalAuthorsList || '',
                'Nhà xuất bản': item.Publisher || '',
                'Năm xuất bản': item.PublicationDate ? new Date(item.PublicationDate).toLocaleDateString('vi-VN') : '',
                'Nơi xuất bản': item.PublicationPlace || '',
                'ISBN': item.Isbn || '',
                'Ngôn ngữ': item.Language || '',
                'Loại công trình': item.ProjectType || '',
                'Cấp đào tạo': item.EducationLevel || '',
                'Quốc gia': item.Country || '',
                'Trạng thái': item.Status || 'Lưu nháp',
                'Người tạo': item.CreatedByEmail || '',
                'Ghi chú': item.Note || ''
            };
        });

        const summaryRow = {
            'STT': '', 'Tên Sách/Giáo trình': 'TỔNG CỘNG:', 'Tác giả': `${dataToExport.length} Công trình`,
            'Nhà xuất bản': '', 'Năm xuất bản': '', 'Nơi xuất bản': '', 'ISBN': '', 'Ngôn ngữ': '',
            'Loại công trình': '', 'Cấp đào tạo': '', 'Quốc gia': '', 'Trạng thái': '', 'Người tạo': '',
            'Ghi chú': ''
        };

        const colWidths = [
            { wch: 5 }, { wch: 45 }, { wch: 30 }, { wch: 25 }, { wch: 15 },
            { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 20 },
            { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 30 }
        ];

        downloadExcel({
            data: formattedData,
            fileName: `DanhSachQuanLySach_${fileSuffix}`,
            sheetName: 'QuanLySach',
            colWidths: colWidths,
            summaryRow: summaryRow
        });
    }, []);

    const handleExportCurrent = () => {
        executeExport(filteredData, 'HienHanh');
        setExportDropdownOpen(false);
    };

    const getFilteredExportData = useCallback(() => {
        let result = [...data];

        if (exportFilters.fromDate) {
            result = result.filter(item => item.PublicationDate && new Date(item.PublicationDate) >= new Date(exportFilters.fromDate));
        }
        if (exportFilters.toDate) {
            result = result.filter(item => item.PublicationDate && new Date(item.PublicationDate) <= new Date(exportFilters.toDate));
        }
        if (exportFilters.projectType) {
            result = result.filter(item => item.ProjectType === exportFilters.projectType);
        }
        if (exportFilters.educationLevel) {
            result = result.filter(item => item.EducationLevel === exportFilters.educationLevel);
        }
        if (exportFilters.userId) {
            const targetId = exportFilters.userId.toString();
            result = result.filter(item => {
                if (item.UserId?.toString() === targetId) return true;
                try {
                    const membersList = JSON.parse(item.MembersJSON || '[]');
                    return membersList.some(m => (m.UserId || m.userId || m.Id)?.toString() === targetId);
                } catch (e) {
                    return false;
                }
            });
        }
        return result;
    }, [data, exportFilters]);

    const handlePreviewAdvancedFilters = () => {
        setPreviewExportData(getFilteredExportData());
    };

    const handleClearAdvancedFilters = () => {
        setExportFilters({ fromDate: null, toDate: null, projectType: '', educationLevel: '', userId: '' });
        setPreviewExportData(null);
    };

    const handleExportFromModal = () => {
        executeExport(previewExportData || getFilteredExportData(), 'LocNangCao');
        setIsExportModalOpen(false);
    };

    const renderDeptTree = (parentId = null, level = 0) => {
        const targetNodes = parentId === null
            ? departments.filter(d => !d.ParentId || !departments.some(p => p.Id === d.ParentId))
            : departments.filter(d => d.ParentId === parentId);

        return targetNodes.map(d => (
            <React.Fragment key={`filter-dept-${d.Id}`}>
                <option value={d.Name}>
                    {'\u00A0'.repeat(level * 4)}{level > 0 ? '˪ ' : ''}{d.Name}
                </option>
                {renderDeptTree(d.Id, level + 1)}
            </React.Fragment>
        ));
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>QUẢN LÝ SÁCH - GIÁO TRÌNH</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: '10px', marginBottom: '20px' }}>
                {canModify && (
                    <button
                        className="btn-add-new"
                        onClick={() => { setEditId(null); setModalMode('add'); }}
                        style={{ margin: 0, width: isDesktop ? 'auto' : '100%' }}
                    >
                        <i className="fa-solid fa-plus"></i> Thêm mới
                    </button>
                )}

                <div style={{ position: 'relative', display: isDesktop ? 'inline-block' : 'block', width: isDesktop ? 'auto' : '100%' }}>
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#1a9953', margin: 0, width: '100%' }}
                        onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                    >
                        <i className="fa-solid fa-file-excel"></i> Xuất Excel <i className="fa-solid fa-caret-down" style={{ marginLeft: '5px' }}></i>
                    </button>

                    {exportDropdownOpen && (
                        <>
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setExportDropdownOpen(false)}></div>
                            <div className="custom-dropdown-menu">
                                <div className="custom-dropdown-item" onClick={handleExportCurrent}>
                                    <i className="fa-solid fa-list-check" style={{ color: '#1a9953', width: '20px', fontSize: '16px' }}></i>
                                    Xuất toàn bộ dữ liệu
                                </div>
                                <div className="custom-dropdown-divider"></div>
                                <div className="custom-dropdown-item" onClick={() => {
                                    setIsExportModalOpen(true);
                                    setExportDropdownOpen(false);
                                    setPreviewExportData(null);
                                }}>
                                    <i className="fa-solid fa-filter" style={{ color: '#f39c12', width: '20px', fontSize: '16px' }}></i>
                                    Lọc dữ liệu nâng cao
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <button
                    className="btn-add-new"
                    style={{ backgroundColor: '#e74c3c', margin: 0, opacity: isCheckingDuplicates ? 0.7 : 1 }}
                    onClick={handleFetchDuplicates}
                    disabled={isCheckingDuplicates}
                >
                    {isCheckingDuplicates ? (
                        <span><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '5px' }}></i> Đang phân tích...</span>
                    ) : (
                        <span><i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '5px' }}></i> Kiểm tra trùng lặp</span>
                    )}
                </button>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <div style={{
                    display: 'flex',
                    flexDirection: isDesktop ? 'row' : 'column',
                    justifyContent: 'space-between',
                    alignItems: isDesktop ? 'center' : 'stretch',
                    marginBottom: '15px',
                    gap: '15px'
                }}>
                    <p className="sub-title" style={{ margin: 0, fontWeight: 'bold', color: '#555' }}>DANH SÁCH CÔNG TRÌNH XUẤT BẢN</p>
                </div>

                <div style={{
                    display: isDesktop ? 'grid' : 'flex',
                    gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'none',
                    flexDirection: isDesktop ? 'none' : 'column',
                    gap: '15px'
                }}>
                    <select name="department" className="form-input" style={{ width: '100%' }} value={filters.department} onChange={handleFilterChange}>
                        <option value="">Chọn đơn vị</option>
                        {renderDeptTree()}
                    </select>

                    <div style={{ width: '100%' }}>
                        <AutoComplete
                            value={selectedAuthor}
                            suggestions={filteredAuthors}
                            completeMethod={searchAuthor}
                            field="label"
                            dropdown
                            placeholder="Họ tên giảng viên"
                            itemTemplate={userFilterTemplate}
                            onChange={(e) => {
                                setSelectedAuthor(e.value);
                                if (e.value && e.value.value) {
                                    setFilters(prev => ({ ...prev, authorId: e.value.value }));
                                } else {
                                    setFilters(prev => ({ ...prev, authorId: null }));
                                }
                            }}
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ width: '100%', padding: '0 12px', border: '1px solid #ced4da', borderRadius: '4px', fontFamily: 'inherit' }}
                            panelStyle={{ width: 'auto', minWidth: '350px', maxWidth: '500px' }}
                            emptyMessage="Không tìm thấy giảng viên"
                        />
                    </div>

                    <select
                        name="status"
                        className="form-input"
                        style={{ width: '100%' }}
                        value={filters.status}
                        onChange={handleFilterChange}
                    >
                        <option value="">Trạng thái</option>
                        <option value="Lưu nháp">Lưu nháp</option>
                        <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                        <option value="Đã duyệt">Đã duyệt</option>
                        <option value="Từ chối">Từ chối</option>
                        <option value="Yêu cầu chỉnh sửa">Yêu cầu chỉnh sửa</option>
                    </select>

                    <div style={{ width: '100%' }}>
                        <Calendar
                            id="fromDate"
                            value={filters.fromDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.value }))}
                            placeholder="Từ ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div style={{ width: '100%' }}>
                        <Calendar
                            id="toDate"
                            value={filters.toDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.value }))}
                            placeholder="Đến ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div className="search-wrapper" style={{ position: 'relative', width: '100%', gridColumn: isDesktop ? 'span 3' : 'none' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888' }}></i>
                        <input
                            type="text"
                            name="keyword"
                            placeholder="Tìm kiếm tiêu đề, NXB..."
                            className="form-input"
                            style={{ width: '100%', paddingLeft: '35px' }}
                            value={filters.keyword}
                            onChange={handleFilterChange}
                        />
                    </div>
                </div>
            </div>

            <BookPublicationListing
                data={filteredData}
                filters={filters}
                onEdit={handleEdit}
                onDelete={handleRequestDelete}
                onRequestDelete={handleRequestDelete}
                onApproveDelete={handleApproveDelete}
                onCancelDeleteRequest={handleCancelDeleteRequest}
                currentUserEmail={currentUser.Email || ''}
                onApprove={(id) => handleStatusChange(id, 'Đã duyệt')}
                onReject={handleRejectWithModal}
                onCancelApprove={(id) => handleStatusChange(id, 'Đã gửi duyệt')}
                onRequestEdit={handleOpenRequestEdit}
                onViewDetail={handleViewDetail}
                onBulkApprove={(ids) => handleBulkStatusChange(ids, 'Đã duyệt')}
                onBulkCancelApprove={(ids) => handleBulkStatusChange(ids, 'Đã gửi duyệt')}
                onBulkDelete={handleBulkDelete}
                isLoading={isLoading}
                toast={toast}
                sortConfig={sortConfig}
                requestSort={(key) => {
                    let direction = 'asc';
                    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
                    setSortConfig({ key, direction });
                }}
            />

            <BookPublicationForm
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                isEditing={modalMode === 'edit' || modalMode === 'view'}
                readOnly={modalMode === 'view'}
                users={users}
                languages={languages}
                countries={countries}
                projectTypes={projectTypes}
                educationLevels={educationLevels}
                members={members}
                setMembers={setMembers}
                toast={toast}
            />

            {isEditRequestModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1050 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Yêu cầu chỉnh sửa Xuất bản</h3>
                            <button className="close-btn" onClick={() => setIsEditRequestModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <form id="editRequestForm" onSubmit={handleSendEditRequest}>
                                <div className="form-group" style={{ marginBottom: '15px', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '5px', borderLeft: '4px solid #3498db' }}>
                                    <label style={{ margin: 0, color: '#333', lineHeight: '1.5' }}>
                                        <strong>Công trình: </strong> {selectedBookForEdit?.Title}
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nội dung cần chỉnh sửa (Sẽ gửi qua Email) *</label>
                                    <textarea
                                        className="form-input"
                                        rows="6"
                                        placeholder="Nhập chi tiết các phần cần giảng viên bổ sung hoặc sửa đổi"
                                        value={editRequestContent}
                                        onChange={(e) => setEditRequestContent(e.target.value)}
                                        style={{ resize: 'vertical' }}
                                        required
                                    ></textarea>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-cancel" onClick={() => setIsEditRequestModalOpen(false)} style={{ margin: 0 }}>Hủy</button>
                            <button type="submit" form="editRequestForm" className="btn-submit" style={{ backgroundColor: '#9b59b6', margin: 0 }}>
                                <i className="fa-solid fa-paper-plane" style={{ marginRight: '8px' }}></i> Gửi Email
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isRejectModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1050 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Từ chối công trình xuất bản</h3>
                            <button className="close-btn" onClick={() => setIsRejectModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <form id="rejectForm" onSubmit={handleConfirmReject}>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Lý do từ chối *</label>
                                    <textarea
                                        className="form-input"
                                        rows="5"
                                        placeholder="Nhập lý do từ chối (sẽ được lưu vào ghi chú và hiển thị cho người dùng)"
                                        value={rejectContent}
                                        onChange={(e) => setRejectContent(e.target.value)}
                                        style={{ resize: 'vertical' }}
                                        required
                                    ></textarea>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-cancel" onClick={() => setIsRejectModalOpen(false)} style={{ margin: 0 }}>Hủy</button>
                            <button type="submit" form="rejectForm" className="btn-submit" style={{ backgroundColor: '#e74c3c', margin: 0 }}>
                                <i className="fa-solid fa-times-circle" style={{ marginRight: '8px' }}></i> Xác nhận từ chối
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isExportModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 900 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '750px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Bộ Lọc & Xuất Excel nâng cao</h3>
                            <button className="close-btn" onClick={() => setIsExportModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Từ ngày (Năm xuất bản)</label>
                                    <Calendar
                                        value={exportFilters.fromDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, fromDate: e.value })}
                                        placeholder="Từ ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        showClear
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '8px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Đến ngày (Năm xuất bản)</label>
                                    <Calendar
                                        value={exportFilters.toDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, toDate: e.value })}
                                        placeholder="Đến ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        showClear
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '8px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Loại công trình</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.projectType} onChange={(e) => setExportFilters({ ...exportFilters, projectType: e.target.value })}>
                                        <option value="">Toàn bộ</option>
                                        {projectTypes.map(pt => <option key={pt.Id} value={pt.Name}>{pt.Name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Trình độ / Cấp đào tạo</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.educationLevel} onChange={(e) => setExportFilters({ ...exportFilters, educationLevel: e.target.value })}>
                                        <option value="">Toàn bộ</option>
                                        {educationLevels.map(el => <option key={el.Id} value={el.Name}>{el.Name}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Thành viên (Nằm trong danh sách)</label>
                                <SearchableFocusDropdown
                                    value={exportFilters.userId}
                                    options={userOptions}
                                    onChange={(e) => setExportFilters({ ...exportFilters, userId: e.value })}
                                    placeholder="Toàn bộ thành viên"
                                    panelUniqueClass="custom-dropdown-panel-book-export-advanced-filter"
                                    className="custom-dropdown"
                                />
                            </div>

                            {previewExportData && (
                                <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                    <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#1a9953' }}>
                                        <i className="fa-solid fa-table-list"></i> Xem trước dữ liệu: Tìm thấy {previewExportData.length} kết quả
                                    </p>
                                    <div style={{ maxHeight: '250px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        <table className="custom-table" style={{ width: '100%', minWidth: '500px', fontSize: '13px', margin: 0 }}>
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                                <tr>
                                                    <th width="50px" style={{ textAlign: 'center' }}>STT</th>
                                                    <th>Tên Sách/Giáo trình</th>
                                                    <th width="150px">Nhà xuất bản</th>
                                                    <th width="100px" style={{ textAlign: 'center' }}>Trạng thái</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewExportData.length > 0 ? (
                                                    previewExportData.map((item, idx) => (
                                                        <tr key={item.Id}>
                                                            <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                            <td><div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontWeight: '500', color: '#0056b3' }}>{item.Title || '---'}</div></td>
                                                            <td><div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>{item.Publisher || '---'}</div></td>
                                                            <td style={{ textAlign: 'center' }}>{item.Status}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Không tìm thấy công trình nào khớp với điều kiện lọc</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '10px' }}>
                            <button type="button" className="btn-cancel" style={{ border: 'none', background: 'none', color: '#e74c3c', margin: 0 }} onClick={handleClearAdvancedFilters}>
                                <i className="fa-solid fa-eraser"></i> Xóa bộ lọc
                            </button>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#3498db', margin: 0 }} onClick={handlePreviewAdvancedFilters}>
                                    <i className="fa-solid fa-magnifying-glass"></i> Lọc & Xem trước
                                </button>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#1a9953', margin: 0 }} onClick={handleExportFromModal}>
                                    <i className="fa-solid fa-download"></i> Xuất Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <DuplicateWarningModal
                isOpen={isDuplicateModalOpen}
                onClose={() => setIsDuplicateModalOpen(false)}
                duplicateGroups={duplicateGroups}
                type="book"
                onDelete={handleRequestDelete}
                currentUserId={currentUser?.Id}
                isAdmin={true}
            />
        </div>
    );
};

export default BookPublication;